import * as React from 'react';

import { enumCameraStatus } from '@rematter/frontend-lib-api-client';
import { CameraRecord } from '@rematter/modules/settings/types/sidecar';
import {
  Badge,
  Button,
  Col,
  Row,
  SearchField,
  ThemeColor,
  TinyTable,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideCamera,
  LucideCpu,
  LucideGlobe,
  LucidePlus,
  LucideRadioTower,
  LucideWrench,
} from 'lucide-react';

import { useCameraEventsSubscription } from '../../../_graphql/subscriptionCameraEvents';
import * as state from '../../../_state/camerasList.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { DemoCameraCreateDialog } from './DemoCameraCreateDialog';
import { RtspCameraCreateDialog } from './RtspCameraCreateDialog';

import { useLibp2p } from '~/shared/providers/libp2p';
import { useHardwarePresenceMembers } from '~/shared/providers/presence';

const STATUS_COLORS: Record<CameraRecord['status'], ThemeColor> = {
  [enumCameraStatus.Discovered]: 'warning',
  [enumCameraStatus.Configured]: 'primary',
};

export const CamerasListView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [showDemoDialog, setShowDemoDialog] = React.useState(false);
  const { connections } = useLibp2p();
  const presenceMembers = useHardwarePresenceMembers();

  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteCameras,
  );

  useCameraEventsSubscription(undefined, {
    onEvent: () => {
      refetch();
    },
  });

  const allCameras = data?.cameras ?? [];

  const cameras = React.useMemo(() => {
    if (!searchTerm.trim()) return allCameras;
    const term = searchTerm.toLowerCase();
    return allCameras.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.driverType.toLowerCase().includes(term) ||
        c.ipAddress?.toLowerCase().includes(term) ||
        c.sidecar?.name.toLowerCase().includes(term),
    );
  }, [allCameras, searchTerm]);

  return (
    <Col gap4 className="@CamerasListView h-full p-4">
      <Row gap2>
        <SearchField
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t.hardwareView.camerasPanel.search.placeholder}
          aria-label={t.hardwareView.camerasPanel.search.ariaLabel}
          className="w-[200px] shrink-0"
        />
        <Button
          className="ml-auto"
          icon={<LucidePlus className="size-4" />}
          onPress={() => setShowDemoDialog(true)}
        >
          {t.hardwareView.demoCreateDialog.actions.addDemoCamera}
        </Button>
        <Button
          icon={<LucidePlus className="size-4" />}
          onPress={() => setShowCreateDialog(true)}
        >
          {t.hardwareView.camerasPanel.actions.addRtspCamera}
        </Button>
      </Row>
      <TinyTable<CameraRecord>
        rowKey={(row) => row.id}
        isLoading={loadingState.isInitialLoading || isFetching}
        error={error}
        onRowClick={(row) => setLocation(`/hardware/cameras/${row.id}`)}
        columns={[
          {
            key: 'name',
            label: t.hardwareView.camerasPanel.columns.name,
            icon: LucideCamera,
            width: 'auto',
            cell: (row) => (
              <div className="truncate font-medium">{row.name}</div>
            ),
          },
          {
            key: 'driverType',
            label: t.hardwareView.camerasPanel.columns.driverType,
            icon: LucideWrench,
            width: 120,
            cell: (row) => <div className="truncate">{row.driverType}</div>,
          },
          {
            key: 'ipAddress',
            label: t.hardwareView.camerasPanel.columns.ipAddress,
            icon: LucideGlobe,
            width: 150,
            cell: (row) => (
              <span className="font-mono text-sm">
                {row.ipAddress ?? '\u2013'}
              </span>
            ),
          },
          {
            key: 'status',
            label: t.hardwareView.camerasPanel.columns.status,
            icon: LucideRadioTower,
            width: 'max-content',
            cell: (row) => {
              const isOnline = presenceMembers.has(row.id);
              const conn = connections.get(row.sidecarId);
              return (
                <Row gap1 className="items-center">
                  <Badge color={STATUS_COLORS[row.status]}>
                    {t.hardwareView.camerasPanel.statusLabels[row.status]}
                  </Badge>
                  <Badge color={isOnline ? 'success' : 'neutral'}>
                    {isOnline
                      ? t.hardwareView.camerasPanel.presenceLabels.online
                      : t.hardwareView.camerasPanel.presenceLabels.offline}
                  </Badge>
                  {conn?.connected && (
                    <Badge
                      color={conn.type === 'direct' ? 'primary' : 'neutral'}
                    >
                      {conn.type === 'direct'
                        ? t.hardwareView.camerasPanel.connectionLabels.direct
                        : t.hardwareView.camerasPanel.connectionLabels.relayed}
                    </Badge>
                  )}
                </Row>
              );
            },
          },
          {
            key: 'sidecar',
            label: t.hardwareView.camerasPanel.columns.sidecar,
            icon: LucideCpu,
            width: 180,
            cell: (row) => (
              <div className="truncate">{row.sidecar?.name ?? '\u2013'}</div>
            ),
          },
        ]}
        data={cameras}
      />
      <RtspCameraCreateDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={() => refetch()}
      />
      <DemoCameraCreateDialog
        isOpen={showDemoDialog}
        onClose={() => setShowDemoDialog(false)}
        onSuccess={() => refetch()}
      />
    </Col>
  );
};
